/**
 * i18next initialization and namespace preload orchestration.
 *
 * ## Namespace tiers (blocking vs background)
 *
 * | Tier | Constant | Blocks first auth paint? | When loaded |
 * | --- | --- | --- | --- |
 * | Pre-login | `PRE_LOGIN_NAMESPACES` | Login only | Cold start on private URLs |
 * | Shell | `SHELL_NAMESPACES` | Yes | Every authenticated route |
 * | Route | registry `i18n` via `appModuleI18n.js` | Yes (current URL) | Per pathname |
 * | Help | `HELP_NAMESPACES` | No | First help modal open |
 * | Embedded widgets | `EMBEDDED_COMPONENT_NAMESPACES` | No | LazyObligationDetails mount |
 * | Background | `POST_LOGIN_NAMESPACES` remainder | No | After authenticated |
 *
 * Route-specific namespaces are derived from `appModuleDefinitions.js` via
 * `appModuleDefinitions.js` → `appModuleI18n.js`. Do not hand-edit
 * `ROUTE_NAMESPACES` here.
 *
 * @module i18n
 * @see docs/translate/preload.md
 * @see docs/runtime-preload.md
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import HttpBackend from "i18next-http-backend";
// Route namespace map is derived from appModuleDefinitions.js (no icon imports).
import { matchRouteNamespaces, ROUTE_NAMESPACES } from "modules/appModuleI18n";
// Standalone list — shared with registry validation without circular imports.
import { ALL_NAMESPACES } from "i18nNamespaces";

const availableLanguages = (process.env.REACT_APP_AVAILABLE_LANGUAGES || "en")
  .split(",")
  .map((lang) => lang.trim())
  .filter(Boolean);

const resolveConfiguredLanguage = (language) => {
  const preferredLanguage = process.env.REACT_APP_PREFERRED_LANGUAGE || "en";
  const fallbackLanguage = availableLanguages.includes(preferredLanguage)
    ? preferredLanguage
    : availableLanguages[0] || "en";

  return availableLanguages.includes(language) ? language : fallbackLanguage;
};

const readStoredLanguage = () => {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem("i18nextLng");
  } catch {
    return null;
  }
};

const preferredLanguage = resolveConfiguredLanguage(readStoredLanguage());

export { ALL_NAMESPACES };

/** Loaded before any authenticated session exists. */
export const PRE_LOGIN_NAMESPACES = ["login"];

/**
 * App chrome namespaces — header, sidebar, mobile shell. Help/tour strings load on
 * demand via HELP_NAMESPACES when the user opens the help modal.
 * Omitted on public routes so external flows stay minimal.
 */
export const SHELL_NAMESPACES = [
  "common",
  "terms",
  "globalSearch",
  "mobileDashboard",
];

/** InstructionsModal + useTour — preloaded on first help click, not at shell gate. */
export const HELP_NAMESPACES = [
  "dashboard",
  "frameworks",
  "contracts",
  "obligationsV2",
  "files",
];

/** Lazy widgets (obligation drawer, AI) — preload on render, not route import. */
export const EMBEDDED_COMPONENT_NAMESPACES = [
  "obligationDetails",
  "ai",
];

export const POST_LOGIN_NAMESPACES = ALL_NAMESPACES.filter(
  (ns) => !PRE_LOGIN_NAMESPACES.includes(ns)
);

/** Public flows skip shell namespaces — see docs/translate/preload.md public route table. */
const PUBLIC_ROUTE_PREFIXES = [
  "/login",
  "/terms-and-conditions",
  "/external/upload",
  "/session/obligation",
  "/required/documents",
  "/supplier/required/documents",
  "/external/actions/login",
];

// Derived from registry — see appModuleI18n.js (do not hand-edit).
export { ROUTE_NAMESPACES };

/** Dedupes concurrent preload calls for the same language + namespace set. */
const preloadPromises = new Map();

const uniqueNamespaces = (namespaces) => [
  ...new Set((namespaces || []).filter((ns) => ALL_NAMESPACES.includes(ns))),
];

const normalizePathname = (pathname = "/") => {
  const [pathWithoutQuery] = String(pathname || "/").split(/[?#]/);
  const normalized = pathWithoutQuery || "/";
  return normalized.startsWith("/") ? normalized : `/${normalized}`;
};

const matchesRoutePrefix = (pathname, prefix) => (
  pathname === prefix || pathname.startsWith(`${prefix}/`)
);

export function isPublicRoute(pathname) {
  const normalizedPathname = normalizePathname(pathname);
  return PUBLIC_ROUTE_PREFIXES.some((prefix) => matchesRoutePrefix(normalizedPathname, prefix));
}

const getCurrentLanguage = () => (
  i18n.resolvedLanguage ||
  i18n.language ||
  preferredLanguage
);

export function getNamespacesForRoute(pathname) {
  const normalizedPathname = normalizePathname(pathname);
  const shellNamespaces = isPublicRoute(normalizedPathname) ? [] : SHELL_NAMESPACES;
  // Route-specific namespaces — resolved via registry-derived longest-prefix map.
  const routeNamespaces = matchRouteNamespaces(normalizedPathname);
  const fallbackNamespaces = routeNamespaces.length ? [] : ["notFound"];

  return uniqueNamespaces([
    ...shellNamespaces,
    ...routeNamespaces,
    ...fallbackNamespaces,
  ]);
}

function getInitialNamespaces() {
  try {
    if (typeof window === "undefined") return PRE_LOGIN_NAMESPACES;

    const pathname = window.location.pathname || "/login";
    return isPublicRoute(pathname)
      ? getNamespacesForRoute(pathname)
      : PRE_LOGIN_NAMESPACES;
  } catch {
    return PRE_LOGIN_NAMESPACES;
  }
}

export function areNamespacesLoaded(namespaces, language = getCurrentLanguage()) {
  return uniqueNamespaces(namespaces).every((namespace) =>
    i18n.hasResourceBundle(language, namespace)
  );
}

export function areRouteNamespacesLoaded(pathname, language = getCurrentLanguage()) {
  return areNamespacesLoaded(getNamespacesForRoute(pathname), language);
}

function loadNamespacesForLanguage(namespaces, language = getCurrentLanguage()) {
  const requestedNamespaces = uniqueNamespaces(namespaces);
  if (!requestedNamespaces.length) return Promise.resolve();

  const pendingNamespaces = requestedNamespaces.filter((namespace) =>
    !i18n.hasResourceBundle(language, namespace)
  );

  if (!pendingNamespaces.length) return Promise.resolve();

  const preloadKey = `${language}:${[...pendingNamespaces].sort().join(",")}`;
  if (preloadPromises.has(preloadKey)) {
    return preloadPromises.get(preloadKey);
  }

  const preloadPromise = initializationPromise
    .then(() => new Promise((resolve) => {
      const connector = i18n.services?.backendConnector;

      if (!connector?.load) {
        i18n.loadNamespaces(pendingNamespaces).then(resolve, (error) => {
          console.error("i18n namespace preload failed", {
            language,
            namespaces: pendingNamespaces,
            error,
          });
          resolve();
        });
        return;
      }

      connector.load(language, pendingNamespaces, (error) => {
        if (error) {
          console.error("i18n namespace preload failed", {
            language,
            namespaces: pendingNamespaces,
            error,
          });
        }
        resolve();
      });
    }))
    .finally(() => {
      preloadPromises.delete(preloadKey);
    });

  preloadPromises.set(preloadKey, preloadPromise);
  return preloadPromise;
}

export function preloadNamespacesForRoute(pathname, language = getCurrentLanguage()) {
  return loadNamespacesForLanguage(getNamespacesForRoute(pathname), language);
}

export function preloadEmbeddedComponentNamespaces(language = getCurrentLanguage()) {
  return loadNamespacesForLanguage(EMBEDDED_COMPONENT_NAMESPACES, language);
}

/** Loads help modal + guided tour namespaces on first FloatButton click. */
export function preloadHelpNamespaces(language = getCurrentLanguage()) {
  return loadNamespacesForLanguage(HELP_NAMESPACES, language);
}

export function preloadRemainingNamespaces(language = getCurrentLanguage()) {
  // Embedded widgets first (many routes mount them lazily), then the rest in background.
  const highPriorityNamespaces = EMBEDDED_COMPONENT_NAMESPACES.filter((namespace) =>
    !i18n.hasResourceBundle(language, namespace)
  );
  const restNamespaces = POST_LOGIN_NAMESPACES.filter((namespace) =>
    !EMBEDDED_COMPONENT_NAMESPACES.includes(namespace) &&
    !i18n.hasResourceBundle(language, namespace)
  );

  return loadNamespacesForLanguage(highPriorityNamespaces, language)
    .then(() => loadNamespacesForLanguage(restNamespaces, language));
}

const INITIAL_NAMESPACES = getInitialNamespaces();

const initializationPromise = i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    lng: preferredLanguage,
    fallbackLng: "en",
    // Public cold starts load only the current public flow; private routes bootstrap from login and then preload their route gate.
    ns: INITIAL_NAMESPACES,
    defaultNS: INITIAL_NAMESPACES[0] || "login",
    interpolation: { escapeValue: false },

    backend: {
      // Files live in public/locales/{{lng}}/{{ns}}.json and are served by the same origin to reduce latency
      loadPath: "/locales/{{lng}}/{{ns}}.json",
    },

    react: {
      useSuspense: true,
    },
  });

export default i18n;
